import {
  isDate,
  isEmpty,
  isString,
  isObject,
  isArray,
  isNumber,
  uniq,
  isFunction,
  has,
} from 'lodash';

import { isValidCompanyName, isValidPersonName } from './name';
import { isValidEmail } from './email';
import { isValidSsnNumber } from './ssn-number';
import { isValidLink } from './url';
import { isValidZipCode } from './zip-code';
import {
  isValidMoney,
  isValidRangeMoneyInputs,
  isValidMinMaxRange,
} from './money';

/**
 * @function typeSpecificValidations
 * @param {string|number|bool} value
 * @param {string} type
 */

/* eslint-disable  @typescript-eslint/no-explicit-any */
function typeSpecificValidations(value: any, type: string, required = false) {
  if (type === 'company_name') {
    return !isValidCompanyName(value as string) || (value as string).length < 3;
  }
  if (type === 'first_name' || type === 'last_name') {
    return !isValidPersonName(value as string);
  }
  if (type === 'email') {
    return !isValidEmail(value as string);
  }
  if (type === 'ssn_number') {
    return !isValidSsnNumber(value as string);
  }
  if (type === 'namedropdown') {
    return !(
      isValid({
        inputValue: (value as { [key: string]: unknown }).dropdown,
        required,
      }) &&
      isValid({
        inputValue: (value as { [key: string]: unknown }).named,
        required,
      })
    );
  }
  if (type === 'link') {
    return !isValidLink(value as string);
  }
  if (type === 'zip_code') {
    return !isValidZipCode(value as string);
  }
  if (type === 'money_range' && isObject(value)) {
    return (
      !isValidRangeMoneyInputs(value, required) || !isValidMinMaxRange(value)
    );
  }
  if (type === 'money' || type === 'money_decimal' || type === 'money_range') {
    if (has(value, 'min') && has(value, 'max')) {
      return (
        !isValidMoney(value.min, required) || !isValidMoney(value.max, required)
      );
    }
    return !isValidMoney(value, required);
  }
  if (type === 'number') {
    return !isNumber(Number.parseFloat(value as string));
  }
  if (type === 'option' && required) {
    return valueDontExist(has(value, 'value') ? value.value : value);
  }
  if (type === 'tags_dropdown' && required) {
    return valueDontExist(has(value, 'value') ? value.value : value);
  }

  if (type === 'skills' && Array.isArray(value)) {
    return value.some(
      ({ section }) => section.id !== '__default__' && !section.name
    );
  }

  return false;
}

export function valueDontExist(value: unknown) {
  if (value === undefined || value === null) {
    return true;
  }

  if (isString(value) && (value.match(/\S/) === null || value === 'none')) {
    return true;
  }

  if (isObject(value) && isEmpty(value)) {
    return true;
  }

  if (isArray(value) && isEmpty(value)) {
    return true;
  }

  return false;
}

/**
 * @function isValid
 * @param {object} property
 * @param {string|number|bool} property.inputValue
 * @param {boolean} property.shouldValidate
 * @param {string} property.inputType
 * @param {boolean} property.required
 * @param {number} property.min
 * @return {boolean}
 */

type IsValidProperty = {
  inputValue: string | number | boolean | unknown;
  shouldValidate?: boolean;
  inputType?: string;
  required?: boolean;
  min?: number;
  max?: number;
  format?: RegExp;
  customValidation?: unknown;
};

export const isValid = function isValid(property: IsValidProperty): boolean {
  const {
    inputValue,
    shouldValidate = true,
    inputType = 'text',
    required = false,
    min,
    max,
    format,
    customValidation = undefined,
  } = property;

  if (!shouldValidate) {
    return true;
  }

  if (!required && valueDontExist(inputValue)) {
    return true;
  }

  if (required && valueDontExist(inputValue)) {
    return false;
  }

  if (format && isString(inputValue) && !format.test(inputValue)) {
    return false;
  }

  if (inputType === 'calendar' && isFunction(customValidation)) {
    return !customValidation();
  }

  if (
    inputType === 'number' &&
    min &&
    isNumber(min) &&
    (inputValue as number) < min
  ) {
    return false;
  }

  if (inputType === 'checkbox') {
    return required ? (inputValue as boolean) : true;
  }

  if (inputType === 'checkbox-list') {
    return !isEmpty(inputValue);
  }

  if (isDate(inputValue)) {
    return true;
  }

  if (inputType === 'money_range' && isFunction(customValidation)) {
    return !customValidation(inputValue);
  }

  if (isObject(inputValue) && isEmpty(inputValue)) {
    return false;
  }

  if (
    (inputType === 'tags' ||
      inputType === 'tags_autocomplete' ||
      inputType === 'tags_dropdown') &&
    ((max && (inputValue as unknown[]).length > max) ||
      (min && (inputValue as unknown[]).length < min))
  ) {
    return false;
  }

  if (typeSpecificValidations(inputValue, inputType, required)) {
    return false;
  }

  return true;
};

/**
 * @function isPropertyInvalid
 * @param {object} property
 * @param {object} property.obj
 * @param {string} property.key
 * @param {string} property.fieldType
 * @param {boolean} property.required
 * @return {boolean} obj[key] is invalid
 */

type IsPropertyInvalid<T> = {
  obj: T;
  key: string;
  fieldType?: string;
  required?: boolean;
  format?: RegExp;
};

export function isPropertyInvalid<T>({
  obj,
  key,
  fieldType = 'text',
  required = false,
  format = undefined,
}: IsPropertyInvalid<T>) {
  const value = obj[key as keyof typeof obj];
  const blankField = valueDontExist(value);

  if (required && blankField) {
    return true;
  }

  if (!required && blankField) {
    return false;
  }

  if (format && isString(value) && !format.test(value)) {
    return true;
  }

  return typeSpecificValidations(value, fieldType, required);
}

/**
 * @function anyPropertyInvalid
 * @param {object} state
 * @param {object} fields
 * @param {function} customValidation if you want to test only an
 * specific field for others return null;
 * @return {boolean} check if any property of state is invalid
 */

type FieldsType = Record<string, FieldDefinition>;

type CustomValidation<T, R> =
  | ((
      definition: string,
      state: T,
      fields: Record<string, unknown> | FieldDefinition
    ) => R)
  | undefined;

type FieldDefinitionProps = {
  inputType?: string;
  required?: boolean;
  format?: RegExp;
};

type FieldDefinition = FieldDefinitionProps | undefined;

export function anyPropertyInvalid<T, R>(
  state: T,
  fields: Record<string, FieldDefinition>,
  customValidation: CustomValidation<T, R> | undefined = undefined
) {
  return Object.keys(fields).some((definition) => {
    const customValidationError = customValidation
      ? customValidation(definition, state, fields[definition])
      : null;

    return (
      customValidationError ||
      isPropertyInvalid({
        obj: state,
        key: definition,
        fieldType: fields[definition]!.inputType,
        required: fields[definition]!.required,
        format: fields[definition]!.format,
      })
    );
  });
}

/**
 * @param {object} state
 * @param {object} fields
 * @param {function} customErrors
 * @return {array} array of error keys
 */

type CustomErrors<T, R extends unknown[]> = undefined | ((state: T) => R);

export function getErrors<T, R extends unknown[]>(
  state: T,
  fields: Record<string, FieldDefinition>,
  customErrors: CustomErrors<T, R> = undefined
): string[] {
  const errors = Object.keys(fields).reduce((acc: unknown[], definition) => {
    const value = state[definition as keyof typeof state];
    const blankField = valueDontExist(value);
    const { required, inputType } = fields[definition] as Record<
      string,
      unknown
    >;

    if (required && blankField) {
      acc.push('missingFieldsValidation');
    }
    if (
      inputType === 'namedropdown' &&
      required &&
      (valueDontExist((value as { [key: string]: unknown }).dropdown) ||
        valueDontExist((value as { [key: string]: unknown }).named))
    ) {
      acc.push('missingFieldsValidation');
    }
    if (!blankField) {
      if (inputType === 'email' && !isValidEmail(value as string)) {
        acc.push('emailInvalid');
      }
      if (inputType === 'link' && !isValidLink(value as string)) {
        acc.push('linkInvalid');
      }
      if (
        inputType === 'password' &&
        !(state as { passwordValid: boolean }).passwordValid
      ) {
        acc.push('passwordWeak');
      }
      if (inputType === 'ssn_number' && !isValidSsnNumber(value as string)) {
        acc.push('ssnInvalid');
      }
      if (inputType === 'zip_code' && !isValidZipCode(value as string)) {
        acc.push('zipInvalid');
      }
      if (inputType === 'company_name') {
        if ((value as string).length < 3) {
          acc.push('companyNameLengthInvalid');
        }
        if (!isValidCompanyName(value as string)) {
          acc.push('companyNameInvalid');
        }
      }
      if (inputType === 'first_name' && !isValidPersonName(value as string)) {
        acc.push('firstNameInvalid');
      }
      if (inputType === 'last_name' && !isValidPersonName(value as string)) {
        acc.push('lastNameInvalid');
      }
      if (
        (inputType === 'money' || inputType === 'money_decimal') &&
        !isValidMoney(value, required)
      ) {
        acc.push('missingFieldsValidation');
      }
      if (
        (inputType === 'option' || inputType === 'tags_dropdown') &&
        required &&
        valueDontExist(has(value, 'value') ? value.value : value)
      ) {
        acc.push('missingFieldsValidation');
      }

      if (inputType === 'skills' && Array.isArray(value)) {
        if (
          value.some(
            ({ section }) => section.id !== '__default__' && !section.name
          )
        ) {
          acc.push('missingFieldsValidation');
        }
      }
    }

    return acc;
  }, []);

  const customErrorKeys = customErrors ? customErrors(state) : [];
  return uniq([...errors, ...customErrorKeys]) as string[];
}

export const anyPropertyFilled = (
  state: Record<string, unknown>,
  fields: FieldsType
) => Object.keys(fields).some((definition) => !isEmpty(state[definition]));
